import React, { useState, useEffect } from "react";
import { Users, Package, Store, ShoppingBag, Trash2, EyeOff, Eye } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { categoryLabel, formatPrice, toNeDigits } from "@/lib/constants";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const AdminDashboard = () => {
  const { t, lang } = useLang();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);

  const loadAll = () => {
    Promise.all([api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/products")]).then(
      ([s, u, p]) => {
        setStats(s.data);
        setUsers(u.data);
        setProducts(p.data);
      }
    );
  };

  useEffect(() => { loadAll(); }, []);

  const toggleProduct = async (id) => {
    await api.patch(`/admin/products/${id}`);
    loadAll();
    toast.success(t("toast.productUpdated"));
  };

  const deleteProduct = async (id) => {
    await api.delete(`/admin/products/${id}`);
    loadAll();
    toast.success(t("toast.productDeleted"));
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB") : "—");
  const roleLabel = { buyer: t("auth.roleBuyer"), seller: t("auth.roleSeller"), admin: t("nav.admin") };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-[#14251F] mb-5">{t("admin.title")}</h1>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 mb-6">
          {[
            { v: stats.total_users, l: t("admin.totalUsers"), icon: Users },
            { v: stats.total_sellers, l: t("admin.totalSellers"), icon: Store },
            { v: stats.total_buyers, l: t("admin.totalBuyers"), icon: ShoppingBag },
            { v: stats.total_products, l: t("admin.totalListings"), icon: Package },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} data-testid={`admin-stat-${i}`} className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                <Icon className="w-5 h-5 text-[#123A2C] mb-2" />
                <div className="text-2xl font-extrabold text-[#123A2C]">{toNeDigits(s.v, lang)}</div>
                <div className="text-xs text-[#6B7280]">{s.l}</div>
              </div>
            );
          })}
        </div>
      )}

      <Tabs defaultValue="listings">
        <TabsList className="mb-4">
          <TabsTrigger value="listings" data-testid="tab-listings">{t("admin.listings")}</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">{t("admin.users")}</TabsTrigger>
          <TabsTrigger value="overview" data-testid="tab-overview">{t("admin.overview")}</TabsTrigger>
        </TabsList>

        {/* Listings */}
        <TabsContent value="listings">
          <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#6B7280] border-b border-[#E5E7EB]">
                  <th className="p-3">{t("admin.product")}</th>
                  <th className="p-3 hidden sm:table-cell">{t("admin.sellerCol")}</th>
                  <th className="p-3">{t("admin.priceCol")}</th>
                  <th className="p-3 hidden md:table-cell">{t("admin.districtCol")}</th>
                  <th className="p-3">{t("admin.statusCol")}</th>
                  <th className="p-3 text-right">{t("admin.action")}</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} data-testid={`admin-product-${p.id}`} className="border-b border-[#F6F7F4]">
                    <td className="p-3 font-medium text-[#14251F]">{p.title}</td>
                    <td className="p-3 text-[#6B7280] hidden sm:table-cell">{p.seller_name}</td>
                    <td className="p-3 text-[#123A2C] font-semibold">{t("common.rs")} {formatPrice(p.price, lang)}</td>
                    <td className="p-3 text-[#6B7280] hidden md:table-cell">{p.district}</td>
                    <td className="p-3">
                      <span className={p.status === "active" ? "text-[#16A34A]" : "text-[#DC2626]"}>{p.status === "active" ? t("dashboard.active") : t("dashboard.removed")}</span>
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <button data-testid={`admin-toggle-${p.id}`} onClick={() => toggleProduct(p.id)} className="p-1.5 rounded-lg text-[#6B7280] hover:bg-[#F6F7F4]" title={t("admin.remove")}>
                        {p.status === "active" ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button data-testid={`admin-delete-${p.id}`} onClick={() => deleteProduct(p.id)} className="p-1.5 rounded-lg text-[#DC2626] hover:bg-red-50" title={t("admin.delete")}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Users */}
        <TabsContent value="users">
          <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#6B7280] border-b border-[#E5E7EB]">
                  <th className="p-3">{t("admin.name")}</th>
                  <th className="p-3">{t("admin.phone")}</th>
                  <th className="p-3 hidden sm:table-cell">{t("admin.email")}</th>
                  <th className="p-3">{t("admin.roleCol")}</th>
                  <th className="p-3 hidden md:table-cell">{t("admin.regDate")}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`admin-user-${u.id}`} className="border-b border-[#F6F7F4]">
                    <td className="p-3 font-medium text-[#14251F]">{u.name}</td>
                    <td className="p-3 text-[#6B7280]">{u.phone}</td>
                    <td className="p-3 text-[#6B7280] hidden sm:table-cell">{u.email || "—"}</td>
                    <td className="p-3"><span className="text-xs px-2 py-0.5 rounded-full bg-[#DCE7E2] text-[#123A2C]">{roleLabel[u.role]}</span></td>
                    <td className="p-3 text-[#6B7280] hidden md:table-cell">{fmtDate(u.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Overview */}
        <TabsContent value="overview">
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                <h3 className="font-bold text-[#14251F] mb-3">{t("admin.byCategory")}</h3>
                {Object.entries(stats.by_category).map(([k, v]) => (
                  <div key={k} className="flex justify-between py-1.5 text-sm border-b border-[#F6F7F4] last:border-0">
                    <span className="text-[#6B7280]">{categoryLabel(k, lang)}</span>
                    <span className="font-semibold text-[#123A2C]">{toNeDigits(v, lang)}</span>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                <h3 className="font-bold text-[#14251F] mb-3">{t("admin.byDistrict")}</h3>
                {stats.by_district.slice(0, 10).map((d) => (
                  <div key={d.district} className="flex justify-between py-1.5 text-sm border-b border-[#F6F7F4] last:border-0">
                    <span className="text-[#6B7280]">{d.district}</span>
                    <span className="font-semibold text-[#123A2C]">{toNeDigits(d.count, lang)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboard;
